import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Trophy, ChevronDown, Swords } from 'lucide-react'
import type { SeasonBracket, BracketTemplate, BracketPlayer } from './types'

interface BracketSelectorProps {
  brackets: SeasonBracket[]
  templates: BracketTemplate[]
  selectedBracket: SeasonBracket | undefined
  selectedTemplate: BracketTemplate | null
  onBracketSelect: (bracketId: string) => void
  // Record game props
  isMember: boolean
  isSeasonActive: boolean
  players: BracketPlayer[]
  onRecordGame: () => void
}

export function BracketSelector({
  brackets,
  templates,
  selectedBracket,
  selectedTemplate,
  onBracketSelect,
  isMember,
  isSeasonActive,
  players,
  onRecordGame,
}: BracketSelectorProps) {
  if (brackets.length === 0) return null

  return (
    <>
      {/* Mobile: Stacked layout */}
      <div className="sm:hidden space-y-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center justify-between gap-2 w-full px-3 py-2.5 bg-muted rounded-lg text-left">
              <div className="flex items-center gap-2 min-w-0">
                <Trophy className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate">
                  {selectedTemplate?.name || 'Select Bracket'}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[calc(100vw-2rem)]">
            {brackets.map((bracket) => {
              const template = templates.find(t => t.templateId === bracket.templateId)
              return (
                <DropdownMenuItem
                  key={bracket.bracketId}
                  onClick={() => onBracketSelect(bracket.bracketId)}
                  className={`py-2.5 ${selectedBracket?.bracketId === bracket.bracketId ? 'bg-accent' : ''}`}
                >
                  <Trophy className="h-4 w-4 mr-2" />
                  {template?.name || 'Unknown'}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        
        {isMember && isSeasonActive && (
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700 text-white w-full h-11"
            onClick={onRecordGame}
            disabled={!selectedBracket || players.length < 2}
          >
            <Swords className="h-4 w-4 mr-2" />
            Record a Game
          </Button>
        )}
      </div>
      
      {/* Desktop: Horizontal layout */}
      <div className="hidden sm:flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground shrink-0">Bracket:</span>
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
            {brackets.map((bracket) => {
              const template = templates.find(t => t.templateId === bracket.templateId)
              return (
                <button
                  key={bracket.bracketId}
                  onClick={() => onBracketSelect(bracket.bracketId)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    selectedBracket?.bracketId === bracket.bracketId
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {template?.name || 'Unknown'}
                </button>
              )
            })}
          </div>
        </div>
        
        {isMember && isSeasonActive && (
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={onRecordGame}
            disabled={!selectedBracket || players.length < 2}
          >
            <Swords className="h-4 w-4 mr-2" />
            Record a Game
          </Button>
        )}
      </div>
    </>
  )
}
